import { useState } from 'react';

import {
  LIST1_STATUSES,
  LIST2_STATUSES,
  LIST2_PERFIELD_STATUSES,
  StatusRow,
  SetBy,
  TicketType,
} from './statuses.data';
import './list-zero-workflow.scss';

const SET_BY_LABEL: Record<SetBy, string> = {
  system: 'Endorsement system',
  global: 'GLOBAL system',
  azadea: 'AZADEA',
};

const TYPE_TITLE: Record<TicketType, string> = {
  AD: 'Addition',
  DE: 'Departure',
  EC: 'Member change',
};

function UsedBadge({ used }: { used: boolean }) {
  return (
    <span className={`status-badge status-badge--${used ? 'used' : 'unused'}`}>
      {used ? 'Active' : 'Deprecated'}
    </span>
  );
}

function TypeBadges({ types }: { types: TicketType[] }) {
  if (types.length === 0) {
    // No code path sets the status any more, so there is no ticket type to name.
    return <span className="type-badges__none">n/a</span>;
  }

  return (
    <span className="type-badges">
      {types.map((type) => (
        <span
          key={type}
          className={`type-badge type-badge--${type.toLowerCase()}`}
          title={TYPE_TITLE[type]}
        >
          {type}
        </span>
      ))}
    </span>
  );
}

function StatusTable({ rows, showSetBy }: { rows: StatusRow[]; showSetBy?: boolean }) {
  if (rows.length === 0) {
    return <p className="status-empty">Nothing to show with the current filter.</p>;
  }

  return (
    <table className="workflow-table status-table">
      <thead>
        <tr>
          <th style={{ width: 70 }}>ID</th>
          <th style={{ width: 120 }}>Ticket type</th>
          <th>Meaning</th>
          {showSetBy && <th style={{ width: 170 }}>Set by</th>}
          <th style={{ width: 150 }}>Status</th>
          <th>Notes</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.id} className={!row.used ? 'status-row--unused' : undefined}>
            <td className="status-table__id">{row.id}</td>
            <td><TypeBadges types={row.types} /></td>
            <td className="status-table__text">{row.meaning}</td>
            {showSetBy && (
              <td className="status-table__text">
                {row.setBy ? SET_BY_LABEL[row.setBy] : ''}
              </td>
            )}
            <td><UsedBadge used={row.used} /></td>
            <td className="status-table__note">{row.note || ''}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function ListZeroStatuses() {
  const [hideUnused, setHideUnused] = useState(false);

  const filter = (rows: StatusRow[]) => (hideUnused ? rows.filter((r) => r.used) : rows);
  const unusedCount = [
    ...LIST1_STATUSES,
    ...LIST2_STATUSES,
    ...LIST2_PERFIELD_STATUSES,
  ].filter((r) => !r.used).length;

  return (
    <div className="list-zero-workflow">

      <section className="workflow-card">
        <h2>Ticket Statuses</h2>
        <p>
          Every ticket carries a status. The list below covers List 1 (the error and waiting
          queue) and List 2 (tickets being endorsed and invoiced).
        </p>

        <p>
          A ticket also carries a type, and most statuses only ever appear on some of them:{' '}
          <span className="type-badge type-badge--ad">AD</span> an addition,{' '}
          <span className="type-badge type-badge--de">DE</span> a departure, and{' '}
          <span className="type-badge type-badge--ec">EC</span> a member change. Two further
          types, CC and CU, still exist in the code but are no longer created, so no status
          below is listed against them.
        </p>

        <label className="status-toggle">
          <input
            type="checkbox"
            checked={hideUnused}
            onChange={(e) => setHideUnused(e.target.checked)}
          />
          Hide deprecated statuses ({unusedCount})
        </label>
      </section>

      <section className="workflow-card">
        <h2>List 1: error and waiting queue</h2>
        <p>These statuses are all set by the endorsement system as it processes the daily list.</p>
        <StatusTable rows={filter(LIST1_STATUSES)} />
      </section>

      <section className="workflow-card">
        <h2>List 2: endorsement and invoicing</h2>
        <p>
          A ticket reaches List 2 once it is clean. From there it is updated by the GLOBAL system
          (which handles the endorsement with the insurer) and by AZADEA (HRMS, HR team, or
          invoicing review).
        </p>
        <StatusTable rows={filter(LIST2_STATUSES)} showSetBy />
      </section>

      {!hideUnused && (
        <section className="workflow-card">
          <h2>List 2: per-field change statuses (100 to 132)</h2>
          <p>
            These come from the old way of recording a member-information change, which created one
            List 2 ticket per changed field. The daily comparison no longer looks at member fields
            at all: the only change it raises is an AZADEA ID change, and that goes straight to
            List 2 at status 20. They are still assigned while draining a change ticket left in
            List 1 by the old flow, but nothing new reaches them.
          </p>
          <StatusTable rows={LIST2_PERFIELD_STATUSES} showSetBy />
        </section>
      )}
    </div>
  );
}
